<template>
  <div class="relative ad-slider-container">
    <!-- Ads slider with aspect ratio container -->
    <div class="relative overflow-hidden w-full aspect-ratio-container">
      <div
          v-for="(ad, index) in ads"
          :key="ad.id"
          class="absolute top-0 left-0 w-full h-full transition-opacity duration-500"
          :style="{
            opacity: currentIndex === index ? 1 : 0,
            zIndex: currentIndex === index ? 1 : 0
          }"
      >
        <!-- Mobile version (scaled to fit height) -->
        <img
            :src="getImageUrl(ad)"
            :alt="ad.title || 'Advertisement'"
            class="w-full h-full sm:hidden object-contain"
            style="object-position: center"
            @error="handleImageError"
        />

        <!-- Desktop version (your current implementation) -->
        <img
            :src="getImageUrl(ad)"
            :alt="ad.title || 'Advertisement'"
            class="hidden sm:block w-full h-full object-contain"
            @error="handleImageError"
        />

        <div v-if="ad.title" class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2 text-white text-sm">
          {{ ad.title }}
        </div>
      </div>

      <!-- Navigation arrows -->
      <button
          v-if="ads.length > 1"
          @click="prevAd"
          class="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full p-1 text-white transition-all duration-300 z-10"
          aria-label="Previous ad"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </button>

      <button
          v-if="ads.length > 1"
          @click="nextAd"
          class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full p-1 text-white transition-all duration-300 z-10"
          aria-label="Next ad"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
        </svg>
      </button>

      <!-- Dots indicator -->
      <div v-if="ads.length > 1" class="absolute bottom-2 left-0 right-0 flex justify-center space-x-2 z-10">
        <button
            v-for="(_, index) in ads"
            :key="index"
            @click="goToSlide(index)"
            :class="[
              'w-2 h-2 rounded-full transition-all duration-300',
              currentIndex === index ? 'bg-primary scale-125' : 'bg-white bg-opacity-50 hover:bg-opacity-75'
            ]"
            :aria-label="`Go to ad ${index + 1}`"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
// Script section remains unchanged
import { ref, onMounted, onUnmounted } from 'vue';

export default {
  name: 'AdSlider',
  props: {
    ads: {
      type: Array,
      required: true
    }
  },
  setup(props) {
    // Same implementation as before
    const currentIndex = ref(0);
    const slideInterval = ref(null);

    const getImageUrl = (item) => {
      if (!item) return '/placeholder-banner.png';
      return item.image_url || `/images/${item.image}`;
    };

    const handleImageError = (e) => {
      e.target.src = '/placeholder-banner.png';
    };

    const nextAd = () => {
      currentIndex.value = (currentIndex.value + 1) % props.ads.length;
      resetInterval();
    };

    const prevAd = () => {
      currentIndex.value = (currentIndex.value - 1 + props.ads.length) % props.ads.length;
      resetInterval();
    };

    const goToSlide = (index) => {
      currentIndex.value = index;
      resetInterval();
    };

    const startInterval = () => {
      if (props.ads.length <= 1) return;

      slideInterval.value = setInterval(() => {
        nextAd();
      }, 5000);
    };

    const resetInterval = () => {
      if (slideInterval.value) {
        clearInterval(slideInterval.value);
        startInterval();
      }
    };

    onMounted(() => {
      startInterval();
    });

    onUnmounted(() => {
      if (slideInterval.value) {
        clearInterval(slideInterval.value);
      }
    });

    return {
      currentIndex,
      getImageUrl,
      handleImageError,
      nextAd,
      prevAd,
      goToSlide
    };
  }
}
</script>

<style scoped>
.ad-slider-container {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 0.75rem;
  overflow: hidden;
  width: 100%;
}

/* Aspect ratio container for 2700x700 images (3.86:1) */
.aspect-ratio-container {
  position: relative;
  padding-bottom: 25.9%; /* 700/2700 = 0.259 or 25.9% */
}

/* Modified aspect ratio for mobile to show more of the image */
@media (max-width: 640px) {
  .aspect-ratio-container {
    padding-bottom: 60%; /* Taller on mobile to fit more of the image height */
  }
}
</style>